import { useEffect, useState } from 'react';
import { Gift, Copy, Check } from 'lucide-react';
import { api } from '../lib/api';

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api('/api/me/referral').then(setData).catch(() => setData(null));
  }, []);

  if (!data?.available) return null;

  const available = data.credits.filter((c) => c.status === 'available');
  const used = data.credits.filter((c) => c.status === 'used');
  const { pending, qualified } = data.referrals;

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {}
  };

  return (
    <section className="mb-10 rounded-lg border border-brand/30 bg-brand-light/40 p-5">
      <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
        <Gift size={14} /> Refer a friend
      </h2>
      <p className="text-sm text-slate-700 mt-2 leading-relaxed">
        Share your code. When someone new buys their first course with it, you each get {data.percent}% off a course.
      </p>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-2">
          <span className="font-mono font-bold tracking-wider text-slate-900">{data.code}</span>
          <CopyButton onClick={() => copy(data.code, 'code')} done={copied === 'code'} label="Copy code" />
        </div>
        <button
          type="button"
          onClick={() => copy(data.link, 'link')}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"
        >
          {copied === 'link' ? <Check size={14} /> : <Copy size={14} />}
          {copied === 'link' ? 'Link copied' : 'Copy share link'}
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat label="Credits to use" value={available.length} />
        <Stat label="Friends who bought" value={qualified} />
        <Stat label="Signed up, not bought yet" value={pending} />
      </dl>

      {available.length > 0 && (
        <p className="mt-3 text-sm text-emerald-700">
          {available.length === 1
            ? `You have a ${available[0].percent}% credit. It applies automatically at your next checkout while signed in.`
            : `You have ${available.length} credits of ${available[0].percent}% each. One applies automatically per course at checkout while signed in.`}
        </p>
      )}
      {used.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          Used: {used.map((c) => c.used_on_course || 'a course').join(', ')}
        </p>
      )}
    </section>
  );
}

function CopyButton({ onClick, done, label }) {
  return (
    <button type="button" onClick={onClick} className="text-slate-500 hover:text-slate-800" aria-label={label} title={label}>
      {done ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 px-2 py-2">
      <dd className="text-lg font-bold text-slate-900">{value}</dd>
      <dt className="text-[11px] leading-tight text-slate-500">{label}</dt>
    </div>
  );
}
